// import {createContext, ReactNode, useContext, useEffect, useState} from "react";
//
// import {Account} from "../models/account";
// import {AccountContactData} from "../models/accountContactData";
// import {AccountMedData} from "../models/accountMedData";
//
// import {getAccountById} from "../api/accountApi";
// import {getAccountContactDataById} from "../api/accountContactDataApi";
// import {getAccountMedDataById} from "../api/accountMedDataApi";
//
// type AccountContextType = {
//     account: Account | null;
//     setAccount: (acc: Account | null) => void;
//
//     contactData: AccountContactData | null;
//     setContactData: (data: AccountContactData | null) => void;
//
//     medData: AccountMedData | null;
//     setMedData: (data: AccountMedData | null) => void;
//
//     logout: () => void;
//
//     // reload mit explizitem Account
//     reloadAccountData: (acc: Account) => Promise<void>;
// };
//
// const AccountContext = createContext<AccountContextType | null>(null);
//
// type AccountProviderProps = {
//     children: ReactNode;
// };
//
// export function AccountProvider({children}: AccountProviderProps) {
//     const [account, _setAccount] = useState<Account | null>(null);
//     const [contactData, setContactData] = useState<AccountContactData | null>(null);
//     const [medData, setMedData] = useState<AccountMedData | null>(null);
//
//     // setAccount: State + LocalStorage
//     const setAccount = (acc: Account | null) => {
//         _setAccount(acc);
//
//         if (acc) {
//             localStorage.setItem("account", JSON.stringify(acc));
//         } else {
//             localStorage.removeItem("account");
//         }
//     };
//
//     const logout = () => {
//         setAccount(null);
//         setContactData(null);
//         setMedData(null);
//     };
//
//     // explizites Nachladen nach Login / mit bekanntem Account
//     const reloadAccountData = async (acc: Account) => {
//         const fresh = await getAccountById(acc.id);
//         setAccount(fresh);
//
//         const c = await getAccountContactDataById(acc.id);
//         const m = await getAccountMedDataById(acc.id);
//
//         setContactData(c);
//         setMedData(m);
//     };
//
//     // Einmaliger Initial-Load aus LocalStorage + Daten nachladen
//     useEffect(() => {
//         const stored = localStorage.getItem("account");
//         if (!stored) return;
//
//         try {
//             const parsed = JSON.parse(stored) as Account;
//             _setAccount(parsed);
//
//             (async () => {
//                 await reloadAccountData(parsed);
//             })();
//         } catch {
//             localStorage.removeItem("account");
//         }
//     }, []);
//
//     return (
//         <AccountContext.Provider
//             value={{
//                 account,
//                 setAccount,
//                 contactData,
//                 setContactData,
//                 medData,
//                 setMedData,
//                 logout,
//                 reloadAccountData
//             }}
//         >
//             {children}
//         </AccountContext.Provider>
//     );
// }
//
// export function useAccount() {
//     const ctx = useContext(AccountContext);
//     if (!ctx) {
//         throw new Error("useAccount must be used inside an AccountProvider");
//     }
//     return ctx;
// }
//
// export function useRequiredAccount() {
//     const ctx = useAccount();
//     if (!ctx.account) {
//         throw new Error("useRequiredAccount used without a logged-in account");
//     }
//     return {
//         account: ctx.account,
//         setAccount: ctx.setAccount,
//         contactData: ctx.contactData,
//         setContactData: ctx.setContactData,
//         medData: ctx.medData,
//         setMedData: ctx.setMedData,
//         logout: ctx.logout,
//         reloadAccountData: ctx.reloadAccountData
//     };
// }
//
// export function useFullAccount() {
//     const ctx = useAccount();
//
//     if (!ctx.account) {
//         throw new Error("useFullAccount used without a logged-in account");
//     }
//     if (!ctx.contactData) {
//         throw new Error("useFullAccount used before contact data was loaded");
//     }
//     if (!ctx.medData) {
//         throw new Error("useFullAccount used before medical data was loaded");
//     }
//
//     return {
//         account: ctx.account,
//         setAccount: ctx.setAccount,
//         contactData: ctx.contactData,
//         setContactData: ctx.setContactData,
//         medData: ctx.medData,
//         setMedData: ctx.setMedData,
//         logout: ctx.logout,
//         reloadAccountData: ctx.reloadAccountData
//     };
// }

import {createContext, ReactNode, useContext, useEffect, useState} from "react";

import {Account} from "../models/account";
import {AccountContactData} from "../models/accountContactData";
import {AccountMedData} from "../models/accountMedData";

import {getAccountById} from "../api/accountApi";
import {getAccountContactDataById} from "../api/accountContactDataApi";
import {getAccountMedDataById} from "../api/accountMedDataApi";

type AccountContextType = {
    account: Account | null;
    setAccount: (acc: Account | null) => void;

    contactData: AccountContactData | null;
    setContactData: (data: AccountContactData | null) => void;

    medData: AccountMedData | null;
    setMedData: (data: AccountMedData | null) => void;

    logout: () => void;

    reloadAccountData: (acc: Account) => Promise<void>;

    loading: boolean;   // 🔥 NEU
};

const AccountContext = createContext<AccountContextType | null>(null);

type AccountProviderProps = {
    children: ReactNode;
};

export function AccountProvider({children}: AccountProviderProps) {
    const [account, _setAccount] = useState<Account | null>(null);
    const [contactData, setContactData] = useState<AccountContactData | null>(null);
    const [medData, setMedData] = useState<AccountMedData | null>(null);

    const [loading, setLoading] = useState(true); // 🔥 NEU

    const setAccount = (acc: Account | null) => {
        _setAccount(acc);

        if (acc) {
            localStorage.setItem("account", JSON.stringify(acc));
        } else {
            localStorage.removeItem("account");
        }
    };

    const logout = () => {
        setAccount(null);
        setContactData(null);
        setMedData(null);
    };

    const reloadAccountData = async (acc: Account) => {
        const fresh = await getAccountById(acc.id);
        setAccount(fresh);

        const c = await getAccountContactDataById(acc.id);
        const m = await getAccountMedDataById(acc.id);

        setContactData(c);
        setMedData(m);
    };

    // Initial-Load
    useEffect(() => {
        const stored = localStorage.getItem("account");
        if (!stored) {
            setLoading(false); // 🔥 wichtig
            return;
        }

        try {
            const parsed = JSON.parse(stored) as Account;
            _setAccount(parsed);

            (async () => {
                await reloadAccountData(parsed);
                setLoading(false); // 🔥 nach dem Laden
            })();
        } catch {
            localStorage.removeItem("account");
            setLoading(false); // 🔥 auch bei Fehler
        }
    }, []);

    return (
        <AccountContext.Provider
            value={{
                account,
                setAccount,
                contactData,
                setContactData,
                medData,
                setMedData,
                logout,
                reloadAccountData,
                loading, // 🔥 NEU
            }}
        >
            {children}
        </AccountContext.Provider>
    );
}

export function useAccount() {
    const ctx = useContext(AccountContext);
    if (!ctx) {
        throw new Error("useAccount must be used inside an AccountProvider");
    }
    return ctx;
}

export function useRequiredAccount() {
    const ctx = useAccount();
    if (!ctx.account) {
        throw new Error("useRequiredAccount used without a logged-in account");
    }
    return {
        account: ctx.account,
        setAccount: ctx.setAccount,
        contactData: ctx.contactData,
        setContactData: ctx.setContactData,
        medData: ctx.medData,
        setMedData: ctx.setMedData,
        logout: ctx.logout,
        reloadAccountData: ctx.reloadAccountData
    };
}

export function useFullAccount() {
    const ctx = useAccount();

    if (!ctx.account) {
        throw new Error("useFullAccount used without a logged-in account");
    }
    if (!ctx.contactData) {
        throw new Error("useFullAccount used before contact data was loaded");
    }
    if (!ctx.medData) {
        throw new Error("useFullAccount used before medical data was loaded");
    }

    return {
        account: ctx.account,
        setAccount: ctx.setAccount,
        contactData: ctx.contactData,
        setContactData: ctx.setContactData,
        medData: ctx.medData,
        setMedData: ctx.setMedData,
        logout: ctx.logout,
        reloadAccountData: ctx.reloadAccountData
    };
}